import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Theme } from '../../layouts/ats-service/service-models/theme';
import { nullSafePath } from '../../shared/helpers/null-safe-path';
import { SessionService } from '../../shared/services/session.service';
import { ApplicantService } from '../applicant-service/applicant.service';
import { JobService } from '../job-service/job.service';
import { JobStatusModel } from '../job-service/models/job-status-model';
import { JobModel } from '../job-service/service-models/JobModel';
import { JobQueried } from '../job-service/service-models/JobQueried';
import { JobStatusService } from '../job-status.service';

@Component({
  selector: 'app-job',
  templateUrl: './job.component.html',
  styleUrls: ['./job.component.scss']
})
export class JobComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  identifier: string;
  subBrandIdentifier: string;
  jsonLD: {};
  jobId: number;
  token: string;
  mapSource: SafeResourceUrl;
  model: JobModel;
  sections: Array<any> = new Array<any>(5);
  shouldDisplayMap: boolean;
  theme: Theme;

  constructor(private route: ActivatedRoute,
    private applicantService: ApplicantService,
    private router: Router,
    private jobService: JobService,
    private jobStatusService: JobStatusService,
    private sanitizer: DomSanitizer,
    private readonly sessionService: SessionService) { }

  apply() {
    this.subscription.add(this.applicantService.getNewApplicantId(this.jobId)
      .subscribe((payload: { applicantId: string }) => {
        let tokenParam = {};

        if (this.token !== undefined && this.token !== '') {
          tokenParam = { queryParams: { token: this.token } };
        }

        localStorage.setItem(this.jobId.toString(), this.model.title);

        this.router.navigate(nullSafePath(
          this.identifier, this.subBrandIdentifier,
          'jobs', this.jobId,
          'apply', payload.applicantId,
          'resume'),
          tokenParam);
        
      }));
  }

  getExperienceDisplayValue(value: string): string {
    const mappedValues = {
      'EntryLevel': 'Entry-Level',
      'MidLevel': 'Mid-Level'
    };

    return mappedValues[value] ? mappedValues[value] : value;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    sessionStorage.removeItem('completeApplicationPartRequestId');
    this.subscription.add(this.route.parent.params.subscribe((params: Params) => {
      this.jobId = params['jobId'];
      this.identifier = params['identifier'];
      this.subBrandIdentifier = params['subBrandIdentifier'];
    }));

    this.subscription.add(this.route.queryParamMap.subscribe(queryParams => {
      this.token = queryParams.get('token');
    }));

    this.subscription.add(this.route.parent.data.subscribe(
      (data: { model: Theme }) => {
        this.theme = data.model;
      }));

    this.subscription.add(this.route.data
      .subscribe((data: { model: JobModel, status: JobStatusModel }) => {
        if (!data.model) {
          this.model = new JobModel();
          this.sections = [];
          this.router.navigate(['../../job-not-found'], { relativeTo: this.route, skipLocationChange: true });
          return;
        }
        
        this.model = Object.assign(new JobModel(), data.model);
        this.sections[this.model.informationElementSortOrders.benefits - 1] = {
          heading: 'Benefits',
          items: this.model.benefits,
          type: 'benefit',
          getItemContent: (item: any) => item,
          getItemHeading: null
        };
        this.sections[this.model.informationElementSortOrders.companyDescriptions - 1] = {
          heading: 'Company Description',
          items: this.model.companyDescriptions,
          type: 'companyDescription',
          getItemContent: (item: any) => item.description,
          getItemHeading: null
        };
        this.sections[this.model.informationElementSortOrders.customs - 1] = {
          heading: null,
          items: this.model.customElements,
          type: 'custom',
          getItemContent: (item: any) => item.description,
          getItemHeading: (item: any) => item.name
        };
        this.sections[this.model.informationElementSortOrders.jobDescriptions - 1] = {
          heading: 'Job Description',
          items: this.model.jobDescriptions,
          type: 'jobDescription',
          getItemContent: (item: any) => item.description,
          getItemHeading: null
        };
        this.sections[this.model.informationElementSortOrders.videos - 1] = {
          heading: null,
          items: this.model.videos,
          type: 'video',
          getItemContent: null,
          getItemHeading: null
        };
        this.model.setDisplayPostedDate();
        this.jobStatusService.checkIfPageAccessible(data.status, true);
        this.jobService.trackApplicantEvent(this.jobId,
          new JobQueried(this.jobId, new Date(), this.sessionService.getSessionId())).subscribe();
        this.jsonLD = {
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          'datePosted': this.model.postedDate,
          'description': this.getJsonLDJobDescription(),
          'employmentType': this.getJsonLDEmploymentType(this.model.employmentType),
          'hiringOrganization': {
            '@type': 'Organization',
            'name': this.model.instanceName,
            'logo': this.theme.logoUri
          },
          'identifier': {
            '@type': 'PropertyValue',
            'name': this.model.instanceName,
            'value': this.model.id
          },
          'jobLocation': {
            '@type': 'Place',
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': this.model.fullLocation.address1,
              'addressLocality': this.model.fullLocation.city,
              'addressRegion': this.model.fullLocation.province,
              'postalCode': this.model.fullLocation.postalCode,
              'addressCountry': 'US'
            }
          },
          ...(this.model.remoteStatus === 'Full' && { jobLocationType: 'TELECOMMUTE' }),
          'title': this.model.title,
          'baseSalary': this.model.compensation.type === 'Fixed' ? {
            '@type': 'MonetaryAmount',
            'currency': 'USD',
            'value': {
              '@type': 'QuantitativeValue',
              'value': this.model.compensation.min,
              'unitText': this.mapGoogleSalaryUnitText()
            }
          } : this.model.compensation.type === 'Range' ? {
            '@type': 'MonetaryAmount',
            'currency': 'USD',
            'value': {
              '@type': 'QuantitativeValue',
              'minValue': this.model.compensation.min,
              'maxValue': this.model.compensation.max,
              'unitText': this.mapGoogleSalaryUnitText()
            }
          } : null
        };

        let address = this.model.fullLocation.address1;
        if (this.model.fullLocation.city) {
          address += `,${this.model.fullLocation.city}`;
        }
        if (this.model.fullLocation.province) {
          address += `,${this.model.fullLocation.province}`;
        }
        if (this.model.fullLocation.postalCode) {
          address += `,${this.model.fullLocation.postalCode}`;
        }

        const googleMapsQuery = encodeURIComponent(address);
        this.mapSource = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.google.com/maps/embed/v1/place?key=${this.model.googleMapsApiKey}&q=${googleMapsQuery}`);

        if (this.model.applicationDeadline) { this.jsonLD['validThrough'] = this.model.applicationDeadline; }
      })
    );
  }

  private mapGoogleSalaryUnitText() {
    if (this.model.compensation.type === 'Other') { return null; }

    switch (this.model.compensation.period) {
      case 'Hourly':
        return 'HOUR';
      case 'Daily':
        return 'DAY';
      case 'Weekly':
        return 'WEEK';
      case 'Monthly':
        return 'MONTH';
      case 'Annual':
        return 'YEAR';
    }
  }

  private getJsonLDEmploymentType(employmentType: string) {
    const employmentTypeMap = {
      'Full-time': 'FULL_TIME',
      'Part-time': 'PART_TIME',
      'Independent Contractor': 'CONTRACTOR',
      'Temporary': 'TEMPORARY',
      'Internship': 'INTERN',
      'Volunteer': 'VOLUNTEER',
      'Per Diem': 'PER_DIEM'
    };

    return employmentTypeMap[employmentType] || 'OTHER';
  }

  private getJsonLDJobDescription() {
    const content: Array<string> = new Array<string>(this.sections.length);

    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      
      if (section.type === 'benefit' && this.model.benefits.length) {
        content[i] = '<b>Benefits</b><br/><br/>' + this.model.benefits.map(x => x).reduce((pre, curr) => pre.concat(curr), '')
          + '<br/><br/>';
      } else if (section.type === 'companyDescription' && this.model.companyDescriptions.length) {
        content[i] = '<b>Company Description</b><br/><br/>'
          + this.model.companyDescriptions.map(x => x.description).reduce((pre, curr) => pre.concat(curr), '') + '<br/><br/>';
      } else if (section.type === 'custom' && this.model.customElements.length) {
        content[i] =
          this.model.customElements.map(x => `<b>${x.name}</b><br/><br/>` + x.description).reduce((pre, curr) => pre.concat(curr), '')
          + '<br/><br/>';
      } else if (section.type === 'jobDescription' && this.model.jobDescriptions.length) {
        content[i] = '<b>Job Description</b><br/><br/>'
          + this.model.jobDescriptions.map(x => x.description).reduce((pre, curr) => pre.concat(curr), '') + '<br/><br/>';
      }
    }

    return content.map(x => x).reduce((pre, curr) => pre.concat(curr), '');
  }

  sanitizeVideo(link: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(link);
  }
}
